import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hosts that count as the canonical TailorFlow domain — sub-zero (`www`,
// `tailorflow.tech`, preview, localhost) MUST return null so the login
// page keeps the default brand. We only treat truly customer-owned hosts
// as branded ones: `<sub>.tailorflow.tech` where `<sub>` is not a reserved
// system subdomain.
const RESERVED_HOSTS = new Set(['www', 'app', 'api', 'preview', 'staging']);

function extractCustomSubdomain(host) {
  if (!host) return null;
  const lower = host.toLowerCase().split(':')[0];

  // Localhost / preview environments — no branding lookup
  if (lower === 'localhost' || lower.endsWith('.localhost')) return null;
  if (lower.endsWith('.preview.emergentagent.com')) return null;
  if (lower.endsWith('.emergent.host')) return null;

  // Direct hits on the apex / www → no custom subdomain
  if (lower === 'tailorflow.tech' || lower === 'www.tailorflow.tech') return null;

  // Custom shop subdomain: `<sub>.tailorflow.tech`
  const match = lower.match(/^([a-z0-9](?:[a-z0-9-]{1,28}[a-z0-9]))\.tailorflow\.tech$/);
  if (!match) return null;
  const sub = match[1];
  if (RESERVED_HOSTS.has(sub)) return null;
  return sub;
}

/**
 * Pre-auth white-label hook. Detects if the current host is a customer's
 * `<sub>.tailorflow.tech` subdomain and fetches that store's branding so
 * the login screen renders with their logo + colours BEFORE auth.
 *
 * Returns `{ branding, loading, subdomain }`:
 *   - `branding` is null when on the apex / preview / localhost.
 *   - `subdomain` is the resolved string or null.
 *   - `loading` is true only while the public lookup is in flight.
 */
export function useSubdomainBranding() {
  const sub = extractCustomSubdomain(typeof window !== 'undefined' ? window.location.host : '');
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(Boolean(sub));

  useEffect(() => {
    if (!sub) return;
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/public/store-by-subdomain/${sub}`)
      .then(({ data }) => { if (!cancelled) setBranding(data); })
      .catch(() => { /* 404 → host is a subdomain but no store owns it; render default brand */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sub]);

  return { branding, loading, subdomain: sub };
}
